import { Plus, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { AgentDetail, MyAgent } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { SearchInput } from "@/components/ui/search-input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { AgentCard } from "@/features/agents/components/AgentCard";
import { AgentStatusBadge } from "@/features/agents/components/AgentStatusBadge";
import { InstalledAgentCard } from "@/features/agents/components/InstalledAgentCard";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import {
  useMyCreatedAgents,
  useMyInstalledAgents,
} from "@/features/marketplace/hooks/useMarketplace";
import { useTeams } from "@/features/teams/hooks/useTeams";

/**
 * M6 / FE8-3: ``/me/agents`` tem 2 tabs (Instalados / Criados por mim) +
 * busca por nome e filtros por time e modelo. Busca e filtro de time valem
 * nas duas abas; o filtro de modelo so se aplica a "Criados" (agentes
 * instalados nao expoem ``llm_model_id``).
 */
export function AgentListPage() {
  const { t } = useTranslation("agents");
  const navigate = useNavigate();
  const { isAdmin, memberships } = useAuth();
  const canEditAnything =
    isAdmin ||
    memberships.some((m) => m.role === "manager" || m.role === "creator");

  const installed = useMyInstalledAgents();
  const created = useMyCreatedAgents();
  const { data: teams } = useTeams({ include_inactive: false });
  const { data: models } = useLlmModels({ include_inactive: true });

  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [view, setView] = useViewMode("agents:view", "cards");

  const teamMap = useMemo(
    () => new Map(teams?.map((tm) => [tm.id, tm.name]) ?? []),
    [teams],
  );

  function openChatFor(agentId: string) {
    navigate(`/threads/new?agent_id=${agentId}`);
  }

  const createdColumns = useMemo<DataTableColumn<AgentDetail>[]>(
    () => [
      {
        key: "name",
        header: t("list.columns.name"),
        render: (a) => (
          <span className="font-medium text-ink-950">{a.name}</span>
        ),
      },
      {
        key: "team",
        header: t("list.columns.team"),
        render: (a) => teamMap.get(a.owner_team_id) ?? "—",
      },
      {
        key: "status",
        header: t("list.columns.status"),
        render: (a) => <AgentStatusBadge status={a.status} />,
      },
      {
        key: "kbs",
        header: t("list.columns.kbs"),
        render: (a) => a.kb_count,
      },
      {
        key: "mcps",
        header: t("list.columns.mcps"),
        render: (a) => a.mcp_count,
      },
      {
        key: "rating",
        header: t("list.columns.rating"),
        render: (a) =>
          a.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-current text-yellow-500" />
              {a.rating_avg?.toFixed(1)} ({a.rating_count})
            </span>
          ) : (
            "—"
          ),
      },
    ],
    [t, teamMap],
  );

  const installedColumns = useMemo<DataTableColumn<MyAgent>[]>(
    () => [
      {
        key: "name",
        header: t("list.columns.name"),
        render: (a) => (
          <span className="font-medium text-ink-950">{a.name}</span>
        ),
      },
      {
        key: "team",
        header: t("list.columns.team"),
        render: (a) => teamMap.get(a.owner_team_id) ?? "—",
      },
      {
        key: "rating",
        header: t("list.columns.rating"),
        render: (a) =>
          a.rating_avg != null ? (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-current text-yellow-500" />
              {a.rating_avg.toFixed(1)}
            </span>
          ) : (
            "—"
          ),
      },
    ],
    [t, teamMap],
  );

  const query = search.trim().toLowerCase();
  function matchesCommon(a: { name: string; owner_team_id: string }) {
    if (teamFilter && a.owner_team_id !== teamFilter) return false;
    if (query && !a.name.toLowerCase().includes(query)) return false;
    return true;
  }

  const filteredInstalled = useMemo(
    () => (installed.data ?? []).filter(matchesCommon),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [installed.data, query, teamFilter],
  );
  const filteredCreated = useMemo(
    () =>
      (created.data ?? []).filter(
        (a) =>
          matchesCommon(a) &&
          (!modelFilter || a.llm_model_id === modelFilter),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [created.data, query, teamFilter, modelFilter],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/marketplace")}>
              {t("list.marketplaceCta")}
            </Button>
            <Button
              onClick={() => navigate("/me/agents/new")}
              disabled={!canEditAnything}
            >
              <Plus className="h-4 w-4" />
              {t("actions.new")}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("filters.searchPlaceholder")}
          className="w-full sm:w-64"
        />
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink-950"
          aria-label={t("filters.ownerTeam")}
        >
          <option value="">{t("filters.allTeams")}</option>
          {teams?.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.name}
            </option>
          ))}
        </select>
        <select
          value={modelFilter}
          onChange={(e) => setModelFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink-950"
          aria-label={t("filters.model")}
        >
          <option value="">{t("filters.allModels")}</option>
          {models?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.display_name}
            </option>
          ))}
        </select>
        <ViewToggle
          value={view}
          onChange={setView}
          labels={{ cards: t("list.viewCards"), list: t("list.viewList") }}
          className="ml-auto"
        />
      </div>

      <Tabs defaultValue="installed">
        <TabsList>
          <TabsTrigger value="installed">{t("tabs.installed")}</TabsTrigger>
          {canEditAnything ? (
            <TabsTrigger value="created">{t("tabs.createdByMe")}</TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="installed" className="space-y-3 pt-3">
          {installed.isLoading ? <LoadingState /> : null}
          {installed.isError ? <ErrorState error={installed.error} /> : null}
          {installed.data && installed.data.length === 0 ? (
            <EmptyState
              title={t("list.noInstalled")}
              description={t("list.noInstalledHint")}
              action={
                <Button onClick={() => navigate("/marketplace")}>
                  {t("list.openMarketplace")}
                </Button>
              }
            />
          ) : installed.data && filteredInstalled.length === 0 ? (
            <EmptyState title={t("filters.noResults")} />
          ) : null}
          {filteredInstalled.length > 0 ? (
            view === "list" ? (
              <DataTable
                columns={installedColumns}
                rows={filteredInstalled}
                getRowKey={(a) => a.id}
                getRowHref={(a) => `/threads/new?agent_id=${a.id}`}
              />
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filteredInstalled.map((a) => (
                  <InstalledAgentCard
                    key={a.id}
                    agent={a}
                    onOpenChat={openChatFor}
                  />
                ))}
              </ul>
            )
          ) : null}
        </TabsContent>

        {canEditAnything ? (
          <TabsContent value="created" className="space-y-3 pt-3">
            {created.isLoading ? <LoadingState /> : null}
            {created.isError ? <ErrorState error={created.error} /> : null}
            {created.data && created.data.length === 0 ? (
              <EmptyState
                title={t("list.noCreated")}
                description={t("list.noCreatedHint")}
              />
            ) : created.data && filteredCreated.length === 0 ? (
              <EmptyState title={t("filters.noResults")} />
            ) : null}
            {filteredCreated.length > 0 ? (
              view === "list" ? (
                <DataTable
                  columns={createdColumns}
                  rows={filteredCreated}
                  getRowKey={(a) => a.id}
                  getRowHref={(a) => `/me/agents/${a.id}`}
                />
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredCreated.map((agent) => (
                    <AgentCard key={agent.id} agent={agent} />
                  ))}
                </ul>
              )
            ) : null}
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
